import React, { useState, useEffect } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Users, Save, Loader2 } from "lucide-react";

const triggerModes = [
  { value: "mention_only", label: "Mention Only", desc: "Reply when bot account is @mentioned" },
  { value: "keyword", label: "Keyword", desc: "Reply when message contains a specific keyword" },
  { value: "slash_command", label: "Slash Command", desc: "Reply when message starts with a command" },
];

export default function GroupSettingsPage() {
  const [settings, setSettings] = useState({
    enabled: false,
    trigger_mode: "mention_only",
    trigger_keyword: "",
    trigger_command: "/bot",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await authAxios.get(`${API}/settings/group`);
        setSettings({
          enabled: data.enabled || false,
          trigger_mode: data.trigger_mode || "mention_only",
          trigger_keyword: data.trigger_keyword || "",
          trigger_command: data.trigger_command || "/bot",
        });
      } catch (err) {
        console.error("Failed to load group settings:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await authAxios.put(`${API}/settings/group`, settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Failed to save group settings:", err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-48" />
          <div className="h-64 bg-slate-200 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-2xl" data-testid="group-settings-page">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-[#0f172a] flex items-center gap-2">
          <Users className="w-6 h-6" />
          Group Settings
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Configure how the bot responds in group chats
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Group Chat Replies</CardTitle>
              <CardDescription className="mt-1">
                When enabled, the bot will respond to messages in groups based on the trigger mode
              </CardDescription>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              data-testid="group-enabled-toggle"
              className="data-[state=checked]:bg-[#22c55e]"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Trigger Mode Selection */}
          <div className="space-y-3">
            <Label>Trigger Mode</Label>
            <div className="grid gap-3">
              {triggerModes.map((mode) => (
                <button
                  key={mode.value}
                  onClick={() => setSettings({ ...settings, trigger_mode: mode.value })}
                  className={`text-left p-3 rounded-lg border-2 transition-colors ${
                    settings.trigger_mode === mode.value
                      ? "border-[#0f172a] bg-slate-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`trigger-mode-${mode.value}`}
                >
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{mode.label}</p>
                    {settings.trigger_mode === mode.value && (
                      <Badge className="bg-[#0f172a] text-white text-xs">Active</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{mode.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Keyword Input */}
          {settings.trigger_mode === "keyword" && (
            <div className="space-y-2">
              <Label htmlFor="trigger_keyword">Trigger Keyword</Label>
              <Input
                id="trigger_keyword"
                placeholder="e.g., help, support, bot"
                value={settings.trigger_keyword}
                onChange={(e) => setSettings({ ...settings, trigger_keyword: e.target.value })}
                data-testid="trigger-keyword-input"
              />
              <p className="text-xs text-muted-foreground">
                The bot will reply when a group message contains this keyword (case-insensitive)
              </p>
            </div>
          )}

          {/* Slash Command Input */}
          {settings.trigger_mode === "slash_command" && (
            <div className="space-y-2">
              <Label htmlFor="trigger_command">Trigger Command</Label>
              <Input
                id="trigger_command"
                placeholder="e.g., /bot, /ask, /help"
                value={settings.trigger_command}
                onChange={(e) => setSettings({ ...settings, trigger_command: e.target.value })}
                data-testid="trigger-command-input"
              />
              <p className="text-xs text-muted-foreground">
                The bot will reply when a group message starts with this command
              </p>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#0f172a] hover:bg-[#1e293b]"
              data-testid="save-group-settings-button"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {saving ? "Saving..." : "Save Settings"}
            </Button>
            {saved && (
              <span className="text-sm text-[#22c55e] font-medium" data-testid="save-success">
                Settings saved
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
